/* eslint-disable no-magic-numbers */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useCountdown, useLatestVersionDetection } from '@futrli/shared-services';

import { LatestVersionRefreshModal } from './components/latest-version-refresh-modal/latest-version-refresh-modal';
import { reloadPage } from './reload-page';

const COUNTDOWN_TIME_MS = 60000; // 1 min
const SNOOZE_TIME_MS = 1800000; // 30 mins

export const LatestVersionAutoRefresh = () => {
  const { newVersionDetected, updateLastReloadSignature } = useLatestVersionDetection();
  const { value: timerValue, start, stop, updateValue } = useCountdown(COUNTDOWN_TIME_MS);
  const [showPopup, setShowPopup] = useState(false);
  const snoozeTimer = useRef<number>();

  const handleWakeUp = useCallback(() => {
    updateValue(COUNTDOWN_TIME_MS);
    setShowPopup(true);
    start();
    snoozeTimer.current = undefined;
  }, [start, updateValue]);

  const handleReload = useCallback(() => {
    stop();
    updateValue(0);
    updateLastReloadSignature();
    reloadPage();
  }, [stop, updateLastReloadSignature, updateValue]);

  const handleSnooze = useCallback(() => {
    setShowPopup(false);
    stop();

    snoozeTimer.current = window.setTimeout(handleWakeUp, SNOOZE_TIME_MS);
  }, [handleWakeUp, stop]);

  // If a new version is detected, show the popup and start the countdown timer
  useEffect(() => {
    if (newVersionDetected) {
      setShowPopup(true);
      start();
    }
  }, [newVersionDetected, start]);

  // Clear snooze timer on unmount
  useEffect(
    () => () => {
      window.clearInterval(snoozeTimer.current);
    },
    []
  );

  // If timer reaches 0, auto-reload
  useEffect(() => {
    if (showPopup && timerValue <= 0) {
      handleReload();
    }
  }, [handleReload, showPopup, timerValue]);

  return (
    <>
      <LatestVersionRefreshModal
        show={showPopup}
        onReload={handleReload}
        onSnooze={handleSnooze}
        timerValue={timerValue}
      />
    </>
  );
};
